import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';
import './GameMemories.css';

const MEMORY_MIN = 20;
const MEMORY_MAX = 500;
const COMMENT_MIN = 5;
const COMMENT_MAX = 300;

const formatDate = (value) => new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: 'short', day: 'numeric'
}).format(new Date(value));

const GameMemories = ({ gameId }) => {
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [nickname, setNickname] = useState(() => localStorage.getItem('rgm_memory_nickname') || '');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [openReply, setOpenReply] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [replying, setReplying] = useState(null);

  const load = useCallback(async (page = 1) => {
    if (!gameId) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/memories/games/${gameId}`, { params: { page, limit: 10 } });
      setItems(response.data.items || []);
      setPageInfo({ page: response.data.page, pages: response.data.pages, total: response.data.total });
    } catch (_error) {
      setNotice({ type: 'error', text: '回忆留言暂时加载失败，请稍后刷新。' });
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => {
    if (nickname.trim()) localStorage.setItem('rgm_memory_nickname', nickname.trim());
  }, [nickname]);

  const submitMemory = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setNotice(null);
    try {
      await apiClient.post(`/memories/games/${gameId}`, { nickname, content });
      setContent('');
      setNotice({ type: 'success', text: '提交成功，留言将在系统审核后可见。' });
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || '提交失败，请稍后再试。' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (event, memoryId) => {
    event.preventDefault();
    setReplying(memoryId);
    setNotice(null);
    try {
      await apiClient.post(`/memories/messages/${memoryId}/comments`, {
        nickname,
        content: replyText[memoryId] || ''
      });
      setReplyText((previous) => ({ ...previous, [memoryId]: '' }));
      setOpenReply(null);
      setNotice({ type: 'success', text: '提交成功，留言将在系统审核后可见。' });
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || '评论提交失败。' });
    } finally {
      setReplying(null);
    }
  };

  const toggleLike = async (kind, id, memoryId = null) => {
    try {
      const endpoint = kind === 'message'
        ? `/memories/messages/${id}/like`
        : `/memories/comments/${id}/like`;
      const response = await apiClient.post(endpoint);
      setItems((previous) => previous.map((item) => {
        if (kind === 'message' && String(item.id) === String(id)) {
          return { ...item, like_count: response.data.count, user_liked: response.data.liked };
        }
        if (kind === 'comment' && String(item.id) === String(memoryId)) {
          return {
            ...item,
            comments: item.comments.map((comment) => String(comment.id) === String(id)
              ? { ...comment, like_count: response.data.count, user_liked: response.data.liked }
              : comment)
          };
        }
        return item;
      }));
    } catch (_error) {
      setNotice({ type: 'error', text: '点赞失败，请稍后再试。' });
    }
  };

  return (
    <section className="game-memories" aria-labelledby={`memories-title-${gameId}`}>
      <h2 className="memory-heading" id={`memories-title-${gameId}`}>回忆留言</h2>

      <form className="memory-form" onSubmit={submitMemory}>
        <textarea
          value={content}
          onChange={(event) => setContent(event.target.value)}
          minLength={MEMORY_MIN}
          maxLength={MEMORY_MAX}
          required
          placeholder="写下你与这个游戏的回忆……"
        />
        <div className="memory-form-actions">
          <input value={nickname} onChange={(event) => setNickname(event.target.value)} maxLength={20} placeholder="昵称（可选）" aria-label="昵称（可选）" />
          <button type="submit" disabled={submitting}>
            {submitting ? '提交中…' : '提交'}
          </button>
        </div>
      </form>

      {notice && <div className={`memory-notice ${notice.type}`} role="status">{notice.text}</div>}

      <div className="memory-list">
        {loading ? (
          <p className="memory-empty">加载中……</p>
        ) : items.length === 0 ? (
          <p className="memory-empty">还没有留言。</p>
        ) : items.map((memory) => (
          <article className="memory-card" key={memory.id}>
            <div className="memory-meta">
              <strong>{memory.nickname}</strong>
              <time dateTime={memory.created_at}>{formatDate(memory.created_at)}</time>
              {memory.edited_at && <span>已编辑</span>}
            </div>
            <p className="memory-content">{memory.content}</p>
            <div className="memory-actions">
              <button className={memory.user_liked ? 'liked' : ''} onClick={() => toggleLike('message', memory.id)} aria-label="点赞这段回忆">
                ♡ <span>{memory.like_count || 0}</span>
              </button>
              <button onClick={() => setOpenReply(openReply === memory.id ? null : memory.id)}>
                评论 <span>{memory.comments?.length || 0}</span>
              </button>
            </div>

            {memory.comments?.length > 0 && (
              <div className="memory-comments">
                {memory.comments.map((comment) => (
                  <div className="memory-comment" key={comment.id}>
                    <div><strong>{comment.nickname}</strong><time dateTime={comment.created_at}>{formatDate(comment.created_at)}</time></div>
                    <p>{comment.content}</p>
                    <button className={comment.user_liked ? 'liked' : ''} onClick={() => toggleLike('comment', comment.id, memory.id)} aria-label="点赞这条评论">
                      ♡ {comment.like_count || 0}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {openReply === memory.id && (
              <form className="memory-reply-form" onSubmit={(event) => submitReply(event, memory.id)}>
                <input value={nickname} onChange={(event) => setNickname(event.target.value)} maxLength={20} placeholder="昵称（可选）" />
                <textarea
                  value={replyText[memory.id] || ''}
                  onChange={(event) => setReplyText((previous) => ({ ...previous, [memory.id]: event.target.value }))}
                  minLength={COMMENT_MIN}
                  maxLength={COMMENT_MAX}
                  required
                  placeholder="写下评论……"
                />
                <button type="submit" disabled={replying === memory.id}>
                  {replying === memory.id ? '提交中…' : '评论'}
                </button>
              </form>
            )}
          </article>
        ))}
      </div>

      {pageInfo.pages > 1 && (
        <nav className="memory-pagination" aria-label="回忆留言分页">
          <button disabled={pageInfo.page <= 1 || loading} onClick={() => load(pageInfo.page - 1)}>上一页</button>
          <span>{pageInfo.page} / {pageInfo.pages}</span>
          <button disabled={pageInfo.page >= pageInfo.pages || loading} onClick={() => load(pageInfo.page + 1)}>下一页</button>
        </nav>
      )}
    </section>
  );
};

export default GameMemories;
